import React, { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'motion/react';
import { StepNavigation } from './step-navigation';
import { StepHeader } from './step-header';
import {
  WelcomeStep,
  ChargedKitStep,
  TestConnectionStep,
  TripodSetupStep,
  EncoderSetupStep,
  PairDevicesStep,
  HdmiCableStep,
  StartStreamingStep,
  SupportStep,
} from './steps';
import { TiltControlStep } from '../shared';

const MotionBox = motion.create(Box);

type StepKey =
  | 'welcome'
  | 'charged-kit'
  | 'test-connection'
  | 'tripod-setup'
  | 'tilt-control'
  | 'encoder-setup'
  | 'pair-devices'
  | 'hdmi-cable'
  | 'start-streaming'
  | 'support';

const STEPS: StepKey[] = [
  'welcome',
  'tilt-control',
  'charged-kit',
  'test-connection',
  'tripod-setup',
  'encoder-setup',
  'pair-devices',
  'hdmi-cable',
  'start-streaming',
  'support',
];

export const V2QSFlow: React.FC = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward

  const currentStep = STEPS[currentStepIndex];
  const isWelcome = currentStep === 'welcome';
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const goToNext = () => {
    if (currentStepIndex < STEPS.length - 1) {
      setDirection(1);
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentStepIndex > 0) {
      setDirection(-1);
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleGetStarted = () => {
    setDirection(1);
    setCurrentStepIndex(1);
  };

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
    }),
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep onGetStarted={handleGetStarted} />;
      case 'charged-kit':
        return <ChargedKitStep />;
      case 'test-connection':
        return <TestConnectionStep />;
      case 'tripod-setup':
        return <TripodSetupStep />;
      case 'tilt-control':
        return <TiltControlStep />;
      case 'encoder-setup':
        return <EncoderSetupStep />;
      case 'pair-devices':
        return <PairDevicesStep />;
      case 'hdmi-cable':
        return <HdmiCableStep />;
      case 'start-streaming':
        return <StartStreamingStep />;
      case 'support':
        return <SupportStep />;
      default:
        return null;
    }
  };

  return (
    <Box position="relative" minH="100vh" bg="white" overflow="hidden">
      {/* Header with logo (not on welcome) */}
      {!isWelcome && <StepHeader />}

      {/* Step Content with Animation */}
      <AnimatePresence mode="wait" custom={direction}>
        <MotionBox
          key={currentStep}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        >
          {renderStep()}
        </MotionBox>
      </AnimatePresence>

      {/* Navigation (not on welcome) */}
      {!isWelcome && (
        <StepNavigation
          onPrevious={goToPrevious}
          onNext={goToNext}
          showPrevious={currentStepIndex > 1}
          showNext={!isLastStep}
          nextLabel="NEXT"
          currentStep={currentStepIndex}
          totalSteps={STEPS.length - 1}
        />
      )}
    </Box>
  );
};
